import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useForm, FormProvider } from 'react-hook-form'

import { Button, CardContent, Grid, Card as MuiCard, Divider as MuiDivider, Typography, Alert } from '@mui/material'
import { spacing, Stack } from '@mui/system'
import { RegisteredTextField } from '../../components/fields/TextField'

import UserApi from '../../api/UserApi'

const Divider = styled(MuiDivider)(spacing)
const Card = styled(MuiCard)(spacing)

/**
 * Password validation rules:
 * - At least 10 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 */
const VALID_PASSWORD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/

const DEFAULT_VALIDATION_PATTERN = {
    value: VALID_PASSWORD,
    message: 'Lösenordet måste innehålla minst 10 tecken, en stor bokstav, en liten bokstav och en siffra',
}

function NewPasswordFields({ label, label2, name = 'newPassword', pattern = DEFAULT_VALIDATION_PATTERN }) {
    const [pw1, setPw1] = useState('')

    name = name || 'newPassword'
    const name2 = name + '2'
    const toBlank = (v) => v || ''
    return (
        <>
            <RegisteredTextField
                name={name}
                label={label}
                type='password'
                validation={{ required: 'Fältet är obligatoriskt', pattern }}
                onChangeValue={(v) => setPw1(v)}
            />
            <RegisteredTextField
                name={name2}
                label={label2}
                type='password'
                validation={{
                    required: 'Fältet är obligatoriskt',
                    validate: (v) => toBlank(v) === toBlank(pw1) || 'Lösenorden matchar inte',
                }}
            />
        </>
    )
}

function UpdatePasswordForm() {
    const formMethods = useForm({ mode: 'onSubmit', shouldFocusError: true })
    const { handleSubmit, reset } = formMethods
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState({ type: '', text: '' })
    const userApi = new UserApi()

    // Handle form submission
    const onSubmit = async (data) => {
        const { password, newPassword } = data

        // Disable the button
        setLoading(true)
        setMessage({ type: '', text: '' })

        try {
            // Call async function to update the password
            const response = await userApi.updateMe({ password: { currentPassword: password, newPassword } })
            if (response.errorCode) {
                if (response.errorCode === 'WrongPassword') {
                    setMessage({ type: 'error', text: 'Felaktigt nuvarande lösenord' })
                } else if (response.errorCode === 'InvalidPassword') {
                    setMessage({ type: 'error', text: 'Ej tillåtet nytt lösenord' })
                } else {
                    setMessage({ type: 'error', text: 'Kunde inte uppdatera lösenordet. Försök igen.' })
                }
                setLoading(false)
                return
            }
            // Show success message
            setMessage({ type: 'success', text: 'Lösenordet är uppdaterat' })

            // Clear form fields
            reset()
        } catch (error) {
            // Show error message
            setMessage({ type: 'error', text: 'Kunde inte uppdatera lösenordet. Försök igen.' })
        }

        // Enable the button
        setLoading(false)
    }

    return (
        <FormProvider {...formMethods}>
            {/* Add form element and attach handleSubmit to onSubmit */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <Card mb={6}>
                    <CardContent>
                        <Stack spacing={3}>
                            <Typography variant='h6' gutterBottom>
                                Byt lösenord
                            </Typography>

                            {/* Show error or success message */}
                            {message.text && (
                                <Alert severity={message.type === 'error' ? 'error' : 'success'}>{message.text}</Alert>
                            )}

                            {/* Current Password Field */}
                            <RegisteredTextField
                                name='password'
                                label='Nuvarande lösenord'
                                type='password'
                                validation={{ required: 'Fältet är obligatoriskt' }}
                            />

                            {/* New Password Fields */}
                            <NewPasswordFields label='Nytt lösenord' label2='Nytt lösenord (igen)' />

                            {/* Submit Button */}
                            <Button
                                variant='contained'
                                color='primary'
                                sx={{ width: 'auto', alignSelf: 'flex-start' }}
                                disabled={loading}
                                type='submit'
                            >
                                {loading ? 'Byter...' : 'Byt lösenord'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </form>
        </FormProvider>
    )
}

function UserProfile() {
    return (
        <React.Fragment>
            <Helmet title='Användarprofil' />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                }}
            >
                Användarprofil
            </Typography>
            <Divider my={6} />
            <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <UpdatePasswordForm />
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default UserProfile
